import React, { useEffect, useState, useRef } from 'react';

// 1. List allowed paths (add more as needed)
const ALLOWED_PATHS = [
  '/agent/register',
  '/agent/dashboard',
  '/agent/my-listings',
  '/agent/add-listing',
  '/agent/profile',
];

function isPathAllowed(pathname) {
  return ALLOWED_PATHS.some(pattern =>
    typeof pattern === 'string'
      ? pattern === pathname
      : pattern instanceof RegExp
        ? pattern.test(pathname)
        : false
  );
}

const GoogleTranslateButton = ({ pathname }) => {
  const [isReady, setIsReady] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef(null);
  const translateElementRef = useRef(null);

  const isAllowed = isPathAllowed(pathname);

  // Initialize Google Translate
  useEffect(() => {
    if (!isAllowed) return;

    // Force English by clearing any existing translation state
    document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    document.cookie = 'googtrans=/en/en; path=/;';
    
    // Remove any existing translation classes
    document.body.classList.remove('translated-ltr', 'translated-rtl');
    
    // Set up Google Translate
    if (!window.googleTranslateElementInit) {
      window.googleTranslateElementInit = () => {
        if (window.google && window.google.translate) {
          new window.google.translate.TranslateElement({
            pageLanguage: 'en',
            includedLanguages: 'en,ja',
            layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
            autoDisplay: false,
          }, 'google_translate_element');
          
          // Wait for initialization
          setTimeout(() => {
            setIsReady(true);
          }, 2000);
        }
      };
      
      // Load the script
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      script.async = true;
      script.onload = () => {
        console.log('Google Translate script loaded');
      };
      script.onerror = () => {
        console.error('Failed to load Google Translate script');
      };
      document.head.appendChild(script);
    } else {
      // Already loaded
      setTimeout(() => {
        setIsReady(true);
      }, 1000);
    }
  }, [isAllowed, pathname]);

  // Handle auto-close timer
  useEffect(() => {
    if (isOpen) {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      // Set new timeout for 15 seconds
      timeoutRef.current = setTimeout(() => {
        setIsOpen(false);
      }, 15000);
    } else {
      // Clear timeout when manually closed
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }

    // Cleanup on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isOpen]);

  // Handle interaction with translate element to reset timer
  const handleTranslateInteraction = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Reset timer for another 15 seconds
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 15000);
  };

  // Toggle translate panel
  const handleToggle = () => {
    if (!isReady) return;
    setIsOpen(!isOpen);
  };

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (translateElementRef.current && 
          !translateElementRef.current.contains(event.target) && 
          !event.target.closest('.translate-sidebar-button')) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (!isAllowed) return null;

  return (
    <>
      {/* Sticky Side Button */}
      <div className="fixed right-0 top-1/2 transform -translate-y-1/2 z-[9999]">
        <button
          onClick={handleToggle}
          disabled={!isReady}
          className={`
            translate-sidebar-button
            flex items-center justify-center
            w-12 h-32 
            bg-[#10c9c0] hover:bg-[#0eb1a9] 
            text-white font-medium text-sm
            rounded-l-lg shadow-lg
            transition-all duration-300 ease-in-out
            ${!isReady ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            ${isOpen ? 'translate-x-0' : 'translate-x-0'}
          `}
          title="翻訳 (Translation)"
        >
          <span className="transform -rotate-90 whitespace-nowrap">
            {!isReady ? '...' : '翻訳'}
          </span>
        </button>
      </div>

      {/* Google Translate Panel */}
      <div 
        ref={translateElementRef}
        className={`
          fixed right-0 top-1/2 transform -translate-y-1/2 z-[9998]
          transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
        `}
        style={{ marginRight: isOpen ? '48px' : '0px' }}
        onMouseEnter={handleTranslateInteraction}
        onMouseLeave={handleTranslateInteraction}
        onClick={handleTranslateInteraction}
      >
        <div className="bg-white rounded-l-lg shadow-xl border border-gray-200 p-4 min-w-[200px]">
          {/* Close button */}
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm font-medium text-gray-700">翻訳</span>
            <button
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-gray-600 text-xl leading-none"
            >
              ×
            </button>
          </div>
          
          {/* Google Translate Element */}
          <div 
            id="google_translate_element"
            className="google-translate-container"
          ></div>
          
          {/* Reload instruction note */}
          <div className="text-xs text-gray-500 mt-2 text-center border-t pt-2">
            翻訳ボタンが表示されない場合は、<br />
            ページを再読み込みしてください
          </div>
          
          {/* Auto-close indicator */}
          <div className="text-xs text-gray-400 mt-2 text-center">
            自動的に閉じます
          </div>
        </div>
      </div>

      {/* Custom styles for Google Translate widget */}
      <style jsx>{`
        .google-translate-container .goog-te-gadget {
          font-family: inherit !important;
          font-size: 14px !important;
        }
        
        .google-translate-container .goog-te-gadget-simple {
          background-color: transparent !important;
          border: 1px solid #e5e7eb !important;
          border-radius: 6px !important;
          padding: 8px !important;
        }
        
        .google-translate-container .goog-te-gadget-simple .goog-te-menu-value {
          color: #374151 !important;
          font-size: 14px !important;
        }
        
        .google-translate-container .goog-te-gadget-simple .goog-te-menu-value span {
          border: none !important;
          background: none !important;
        }
        
        .google-translate-container .goog-te-combo {
          margin: 0 !important;
          padding: 6px 8px !important;
          border: 1px solid #d1d5db !important;
          border-radius: 4px !important;
          font-size: 14px !important;
          width: 100% !important;
        }
        
        /* Hide Google Translate attribution */
        .google-translate-container .goog-te-gadget-simple .goog-te-menu-value span:first-child {
          display: none !important;
        }
        
        /* Hide the "Powered by" text */
        .google-translate-container .goog-logo-link {
          display: none !important;
        }
        
        .google-translate-container .goog-te-gadget .goog-te-menu-value {
          margin: 0 !important;
        }
      `}</style>
    </>
  );
};

export default GoogleTranslateButton;